/**
 * Drag&Drop zwischen den Board-Gruppen - `enableBoardDragAndDrop` (index.html:6952-6988), aufgerufen
 * am Ende von `renderBoard` (7065).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `row.draggable = !!order && !order.archived` (6956) - KEINE Rechtepruefung. Bericht Task 15,
 *    Befund B1: weder `enableBoardDragAndDrop` noch `moveOrderToBoardGroup` lesen `hasPermission`
 *    oder `edit_orders`. Jeder Nutzer mit Boardsicht darf ziehen.
 * 2. `dragstart` bricht ab (kein `dataTransfer.setData`, kein Klassenwechsel), wenn das Ziel ein
 *    interaktives Element innerhalb der Zeile ist (`event.target.closest('input,select,button,a,
 *    details,summary')`, 6960) - sonst startete das Ziehen auch beim Bedienen einer Zellen-Auswahl.
 * 3. `board.classList.add('dragging')` passiert SOFORT (6959); `row.classList.add('order-list-
 *    dragging')` dagegen erst im naechsten Frame (`requestAnimationFrame`, 6964) - sonst enthielte
 *    das vom Browser gezeichnete Drag-Vorschaubild bereits den abgeblendeten Zustand.
 * 4. `dragend` (6967-6971) raeumt drei Dinge ab: die eigene `order-list-dragging`-Klasse, `dragging`
 *    auf dem Board-Wurzelknoten, und JEDE `board-group-drag-over`-Klasse im Board - auch dann, wenn
 *    das Ziehen ohne Ablegen endet (abgebrochenes Ziehen laesst dadurch nichts zurueck).
 * 5. `dragover` ruft `preventDefault()` (sonst nimmt der Browser den Drop nicht an), setzt
 *    `dropEffect='move'` und markiert die Gruppe (6974-6978). `dragleave` nimmt die Markierung nur
 *    weg, wenn das Ziel den Zeiger wirklich verlassen hat (`!groupBody.contains(event.relatedTarget)`,
 *    6980) - ein Wechsel zwischen Kindknoten derselben Gruppe loest sie NICHT kurz aus.
 * 6. `drop` liest die Id aus `event.dataTransfer.getData('text/plain')` (6984) und ruft bei einer Id
 *    `moveOrderToBoardGroup(id, groupBody.dataset.boardGroup)` (6985) - sonst passiert nichts.
 *
 * ================= Warum hier zusaetzlich eine `Ref` traegt, was im Original `dataTransfer` traegt
 * =================
 *
 * jsdom (Stand dieses Repos, `jsdom@29.1.1`) implementiert `DataTransfer`/`DragEvent` NICHT -
 * `new window.DataTransfer()` wirft `TypeError: … is not a constructor`. `@testing-library/dom`
 * dokumentiert das selbst (`dist/events.js`, Kommentar „DataTransfer is not supported in jsdom") und
 * liefert bei `fireEvent.dragStart(...)` ohne eigenes `dataTransfer`-Init ein Ereignis, dessen
 * `event.dataTransfer` `undefined` bleibt. Ein woertlicher Port, der ungeschuetzt
 * `event.dataTransfer.getData(...)` aufruft, bricht deshalb unter Test mit einem `TypeError`, nicht
 * mit einem Zusicherungsfehlschlag (Bericht, B2). Die Loesung traegt zwei Wege parallel: das echte
 * `dataTransfer` (Optional Chaining, im Browser voll wirksam - `setData`/`effectAllowed`/`dropEffect`
 * bleiben gesetzt, fuer denselben nativen Dragvorgang und ein plausibles Vorschaubild) UND eine
 * `ZeilenUmzugRef`, die dieselbe Id haelt. Unter jsdom greift nur der zweite; im Browser tragen beide
 * dasselbe Ergebnis - mechanische Gleichwertigkeit, wie schon `applyOrderListWidths` in Task 14 (B8
 * dort).
 */
import type { Order } from "../../lib/types.ts";
import { orderListZustand } from "../../lib/orderList.ts";

/** Traegt die gezogene Id ueber `dragstart` -> `drop` hinweg - siehe Kopfkommentar. */
export interface ZeilenUmzugRef {
  aktuelleId: string | null;
}

export function neueZeilenUmzugRef(): ZeilenUmzugRef {
  return { aktuelleId: null };
}

/** Der Board-Wurzelknoten - `$('#board')` im Original, hier `.grouped-order-list`. */
function boardWurzel(knoten: Element): Element | null {
  return knoten.closest(".grouped-order-list");
}

/** `dragstart`-Zuhoerer der Zeile (index.html:6957-6966). Eigenheit 1-3. */
export function zeileZiehenStart(
  ereignis: React.DragEvent<HTMLTableRowElement>,
  order: Order | null | undefined,
  ref: ZeilenUmzugRef,
): void {
  const ziel = ereignis.target as HTMLElement;
  // Eigenheit 2.
  //
  // AUFTRAG 07 (E5): die Bedingung stand hier bis dahin auf `order.archived` - "Archivierte sind
  // nicht ziehbar". Sie traegt jetzt dieselbe Ableitung wie Sichtbarkeit, Auswahlsperre und
  // Schreibschutz (`orderListZustand`, `lib/orderList.ts`): `deleted` schlaegt `archived`, eine in
  // JTL geloeschte Bestellung ist also ebenfalls nicht ziehbar. Die Pruefung steht TROTZ
  // `draggable={false}` am `<tr>` hier noch einmal: eine Bestellung kann WAEHREND des Zugs aus der
  // Sitzung heraus in JTL geloescht werden (SSE) - dann ist `draggable` am bereits gezeichneten
  // Knoten noch `true`, und nur dieser Guard verhindert den Schreibversuch.
  if (
    !order ||
    orderListZustand(order) !== "active" ||
    ziel.closest("input,select,button,a,details,summary")
  ) {
    ereignis.preventDefault();
    return;
  }
  const id = order.id as string;
  ref.aktuelleId = id;
  ereignis.dataTransfer?.setData("text/plain", id);
  if (ereignis.dataTransfer) ereignis.dataTransfer.effectAllowed = "move";
  const zeile = ereignis.currentTarget;
  // Eigenheit 3: `board.dragging` sofort, `order-list-dragging` erst im naechsten Frame.
  boardWurzel(zeile)?.classList.add("dragging");
  requestAnimationFrame(() => zeile.classList.add("order-list-dragging"));
}

/** `dragend`-Zuhoerer der Zeile (index.html:6967-6971). Eigenheit 4 - raeumt IMMER auf. */
export function zeileZiehenEnde(
  ereignis: React.DragEvent<HTMLTableRowElement>,
  ref: ZeilenUmzugRef,
): void {
  const zeile = ereignis.currentTarget;
  zeile.classList.remove("order-list-dragging");
  const board = boardWurzel(zeile);
  board?.classList.remove("dragging");
  board?.querySelectorAll(".board-group-drag-over").forEach((gruppe) => gruppe.classList.remove("board-group-drag-over"));
  ref.aktuelleId = null;
}

/** `dragover`-Zuhoerer der Gruppe (index.html:6974-6978). Eigenheit 5. */
export function gruppeUeberfahren(ereignis: React.DragEvent<HTMLTableSectionElement>): void {
  ereignis.preventDefault();
  if (ereignis.dataTransfer) ereignis.dataTransfer.dropEffect = "move";
  ereignis.currentTarget.classList.add("board-group-drag-over");
}

/** `dragleave`-Zuhoerer der Gruppe (index.html:6979-6981). Eigenheit 5. */
export function gruppeVerlassen(ereignis: React.DragEvent<HTMLTableSectionElement>): void {
  if (!ereignis.currentTarget.contains(ereignis.relatedTarget as Node | null)) {
    ereignis.currentTarget.classList.remove("board-group-drag-over");
  }
}

/** Der Patch, den `moveOrderToBoardGroup` (index.html:6629-6636) auf die Bestellung schreibt. */
export interface ZeilenUmzugErgebnis {
  id: string;
  patch: { boardGroupOverride: string };
}

/**
 * `drop`-Zuhoerer der Gruppe (index.html:6982-6987). Liefert das Ergebnis als reinen Wert zurueck -
 * das Schreiben (`save()`), das Neuzeichnen (`renderBoard()`) und die Meldung (`toast(...)`) bleiben
 * beim Aufrufer (`BoardSeite`), analog zu `mitGesetzterBreite` in `SpaltenBreiten.ts` (Task 14).
 */
export function zeileAbgelegt(
  ereignis: React.DragEvent<HTMLTableSectionElement>,
  gruppeId: string,
  ref: ZeilenUmzugRef,
): ZeilenUmzugErgebnis | null {
  ereignis.preventDefault();
  ereignis.currentTarget.classList.remove("board-group-drag-over");
  const id = ereignis.dataTransfer?.getData("text/plain") || ref.aktuelleId || "";
  if (!id) return null;
  return { id, patch: { boardGroupOverride: gruppeId } };
}
